import { useContext, useMemo } from "react";

import { DataContext } from "contexts/Data";
import { StreamContext } from "contexts/Stream";
import useVariant from "./useVariant";
import useSelectedColor from "./useSelectedColor";

import type { ColorOption, StreamTypes, Variant, ViewTypes } from "types/app";

type GtagVariantData = {
  event_category: string;
  event_label: string;
  variant_id: Variant["id"];
  variant_code: Variant["code"];
  selected_color: ColorOption["label"];
  selected_environment: string;
  variant_pack_type: Variant["variant"];
  variant_seat_type: Variant["seating"];
  variant_wheel_type: Variant["wheels"];
  variant_battery_type: Variant["battery"];
  stream_type: StreamTypes;
  view_type: ViewTypes;
} & Record<string, string>;

/**
 * Only use this hook under DataContext
 */
export default function useGtagVariantData():
  | ((
      eventCategory: string,
      eventLabel: string,
      customParams?: Record<string, string>,
    ) => GtagVariantData)
  | (() => Record<string, never>) {
  const { cachedState } = useContext(DataContext);
  const { streamViewType } = useContext(StreamContext);
  const variant = useVariant();
  const selectedColor = useSelectedColor();

  return useMemo(() => {
    if (!variant) return () => ({});

    return (
      eventCategory: string,
      eventLabel: string,
      customParams?: Record<string, string>,
    ): GtagVariantData => ({
      event_category: eventCategory,
      event_label: eventLabel,
      variant_id: variant.id,
      variant_code: variant.code,
      selected_color: selectedColor.label,
      selected_environment: cachedState.animations.dayMode ? "day" : "night",
      variant_pack_type: variant.variant,
      variant_seat_type: variant.seating,
      variant_wheel_type: variant.wheels,
      variant_battery_type: variant.battery,
      stream_type: streamViewType,
      view_type: cachedState.view,

      ...customParams,
    });
  }, [variant, selectedColor, streamViewType, cachedState.view, cachedState.animations.dayMode]);
}
